/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useMemo, useRef, MutableRef, Ref } from 'preact/hooks';
import { Keys } from '../../utils/UNSAFE_keys';
import { mergeProps } from '../../utils/UNSAFE_mergeProps';
import { ColumnResizeInfo, SizingInfo, TableColumn } from '../Table';
import { useFocusHandling } from './useFocusHandling';
import { useKeyboardHandling } from './useKeyboardHandling';
import { usePointerHandling } from './usePointerHandling';
import { useScrollHandling } from './useScrollHandling';
import { SelectionMode } from '../../UNSAFE_Collection';
import {
  Cell,
  ColumnWidths,
  CurrentCellDetail,
  RowActionDetail,
  SortCriterionDetail,
  ColumnResizeDetail,
  TableSelectionDetail
} from '../../UNSAFE_TableView';

type UseInteractionManagerOptions<K, D, C extends string> = {
  outerTableRef: Ref<HTMLDivElement>;
  tableId: string;
  data: D[] | null;
  isRtl: boolean;
  columnsArray: TableColumn<K, D, C>[];
  appliedColumnWidths: ColumnWidths<C>;
  sizingInfoRef: MutableRef<SizingInfo>;
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>;
  stickyEdgesState: { start?: number; end?: number };
  setStickyEdgesState: (stickyEdgesState: { start?: number; end?: number }) => void;
  cellHasRenderer: (cell: Cell<K, C>) => boolean;
  fullColumnWidths?: number[];
  onColumnResizing?: (detail: ColumnResizeDetail<C>) => void;
  onColumnResize?: (detail: ColumnResizeDetail<C>) => void;
  currentCellOverride?: Cell<K, C>;
  onPersistCurrentCell?: (detail: CurrentCellDetail<K, C>) => void;
  selectionMode?: { row?: SelectionMode; column?: SelectionMode };
  selected?: { row?: Keys<K>; column?: Keys<C> };
  onSelectionChange?: (detail: TableSelectionDetail<K, C>) => void;
  onRowAction?: (detail: RowActionDetail<K, D>) => void;
  sortCriterion?: { key: C; sortDirection: 'ascending' | 'descending' }[];
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void;
};

/**
 * Hook that manages interactions and events on the Preact Table
 */
export function useInteractionManager<K extends string | number, D, C extends string>({
  outerTableRef,
  tableId,
  data,
  isRtl,
  columnsArray,
  appliedColumnWidths,
  sizingInfoRef,
  resizeInfoRef,
  stickyEdgesState,
  setStickyEdgesState,
  cellHasRenderer,
  fullColumnWidths,
  onColumnResizing,
  onColumnResize,
  currentCellOverride,
  onPersistCurrentCell,
  selectionMode,
  selected,
  onSelectionChange,
  onRowAction,
  sortCriterion,
  onSortCriterionChange
}: UseInteractionManagerOptions<K, D, C>) {
  // tracks the current selection extension element (if any)
  const selectionExtensionElementRef = useRef<HTMLElement>();

  // intercept onSelectionChange calls to ensure local selectionExtensionElementRef is cleared
  const onTableSelectionChange = useMemo(() => {
    return onSelectionChange != null
      ? (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => {
          if (!isExtendableSelection) {
            selectionExtensionElementRef.current = undefined;
          }
          onSelectionChange(detail);
        }
      : undefined;
  }, [onSelectionChange]);

  // setup focus tracking using the useFocusHandling hook
  const {
    focusHandlingProps,
    currentCell,
    onCurrentCellChanged,
    isShowFocusRing,
    isTabbableMode,
    onStartFocusTracking,
    onFocusStartEdge,
    onFocusEndEdge
  } = useFocusHandling({
    outerTableRef,
    tableId,
    columnsArray,
    sizingInfoRef,
    selectionExtensionElementRef,
    isRtl,
    cellSupportsTabbable: cellHasRenderer,
    fullColumnWidths,
    currentCellOverride,
    onPersistCurrentCell
  });

  const keyboardHandlingProps = useKeyboardHandling({
    outerTableRef,
    tableId,
    data,
    isRtl,
    isTabbableMode,
    columnsArray,
    sizingInfoRef,
    selectionExtensionElementRef,
    fullColumnWidths,
    currentCell,
    onCurrentCellChanged,
    selectionMode,
    selected,
    onTableSelectionChange,
    onRowAction,
    sortCriterion,
    onSortCriterionChange
  });

  const pointerHandlingProps = usePointerHandling({
    outerTableRef,
    tableId,
    data,
    isRtl,
    columnsArray,
    appliedColumnWidths,
    resizeInfoRef,
    selectionExtensionElementRef,
    fullColumnWidths,
    onColumnResizing,
    onColumnResize,
    currentCell,
    selectionMode,
    selected,
    onTableSelectionChange,
    onRowAction
  });

  const scrollHandlingProps = useScrollHandling({
    outerTableRef,
    columnsArray,
    stickyEdgesState,
    setStickyEdgesState,
    fullColumnWidths
  });

  const interactionManagerProps = mergeProps(
    focusHandlingProps,
    keyboardHandlingProps,
    pointerHandlingProps,
    scrollHandlingProps
  );

  return {
    interactionManagerProps,
    onTableSelectionChange,
    isShowFocusRing,
    isTabbableMode,
    currentCell,
    onStartFocusTracking,
    onFocusStartEdge,
    onFocusEndEdge
  };
}
